import { useState } from "react";
import { useTranslation } from "react-i18next";

import { isActivePipelineFulfillmentStatus } from "@nexus/contracts";
import { Button, Label, Textarea } from "@nexus/ui";

import { FulfillmentStatusBadge } from "@/features/marketplace/components/FulfillmentStatusBadge";
import { OrderScriptActivityLog } from "@/features/marketplace/components/OrderScriptActivityLog";
import { filterActivityForScriptReview } from "@/features/marketplace/lib/order-activity";
import type { MarketplaceOrderScriptByReviewLink } from "@/features/marketplace/types/marketplace.types";
import { isOpsMoveToRevisionDisabled } from "@/features/script-review/lib/ops-revision-awaiting-partner";
import {
  useAddOrderScriptPreflightComment,
  useOrderScriptPreflightComments,
  useSetOrderNoteReaction,
} from "@/lib/queries";

interface SendPartnerRevisionPanelProps {
  scriptReviewId: string;
  link: MarketplaceOrderScriptByReviewLink;
}

export function SendPartnerRevisionPanel({ scriptReviewId, link }: SendPartnerRevisionPanelProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState("");
  const { data: comments = [] } = useOrderScriptPreflightComments(
    link.orderId,
    link.orderScriptId,
    { audience: "admin", tenantId: link.tenantId },
    true,
  );
  const sendRevision = useAddOrderScriptPreflightComment();
  const noteReaction = useSetOrderNoteReaction("admin");
  const moveToRevisionDisabled = isOpsMoveToRevisionDisabled({
    fulfillmentStatus: link.fulfillmentStatus,
    deliverableReviewStatus: link.deliverableReviewStatus,
  });
  const scopedComments = filterActivityForScriptReview(comments, scriptReviewId);

  if (!isActivePipelineFulfillmentStatus(link.fulfillmentStatus)) {
    return null;
  }

  const handleSend = () => {
    if (moveToRevisionDisabled) return;
    const body = draft.trim();
    if (!body) return;
    sendRevision.mutate(
      {
        orderId: link.orderId,
        orderScriptId: link.orderScriptId,
        tenantId: link.tenantId,
        body,
        requestRevision: true,
        ...(link.deliverableId ? { deliverableId: link.deliverableId } : {}),
      },
      {
        onSuccess: () => setDraft(""),
      },
    );
  };

  return (
    <section
      className="flex flex-col gap-3 rounded-lg border border-border bg-muted/20 p-4"
      aria-labelledby={`partner-revision-heading-${scriptReviewId}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 flex-col gap-1">
          <h2
            id={`partner-revision-heading-${scriptReviewId}`}
            className="text-sm font-semibold tracking-tight"
          >
            {t("scriptReview.partnerRevision.heading", "Send back to partner for revision")}
          </h2>
          <p className="text-xs text-muted-foreground">
            {t(
              "scriptReview.partnerRevision.hint",
              "Your comment is shared with the production partner and moves the order to Sent for revision.",
            )}
          </p>
        </div>
        <FulfillmentStatusBadge status={link.fulfillmentStatus} viewer="ops" />
      </div>

      {link.revisionNote ? (
        <p className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground">
            {t("scriptReview.partnerRevision.currentNote", "Latest revision note")}:
          </span>{" "}
          {link.revisionNote}
        </p>
      ) : null}

      {scopedComments.length > 0 ? (
        <OrderScriptActivityLog
          entries={scopedComments}
          viewer="ops"
          onReactionChange={(entry, reactionType, active) =>
            noteReaction.setReaction({
              tenantId: link.tenantId,
              orderId: link.orderId,
              orderScriptId: link.orderScriptId,
              commentId: entry.id,
              reactionType,
              active,
            })
          }
          isReactionPending={(entry, reactionType) =>
            noteReaction.isPending({
              tenantId: link.tenantId,
              orderId: link.orderId,
              orderScriptId: link.orderScriptId,
              commentId: entry.id,
              reactionType,
              active:
                entry.reactions?.find(
                  (group) => group.reactionType === reactionType,
                )?.isCurrentPersonActive ?? false,
            })
          }
        />
      ) : null}

      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`partner-revision-comment-${scriptReviewId}`} className="text-xs">
          {t("scriptReview.partnerRevision.commentLabel", "Revision comments for the partner")}
        </Label>
        <Textarea
          id={`partner-revision-comment-${scriptReviewId}`}
          rows={3}
          value={draft}
          maxLength={500}
          disabled={moveToRevisionDisabled || sendRevision.isPending}
          placeholder={t(
            "scriptReview.partnerRevision.commentPlaceholder",
            "Explain what the partner should change before resubmitting preflight",
          )}
          onChange={(event) => setDraft(event.target.value)}
        />
        {moveToRevisionDisabled ? (
          <p className="text-xs text-muted-foreground" role="status">
            {t(
              "scriptReview.partnerRevision.moveDisabledHint",
              "This script is already sent for revision. Move to revision is unavailable until the partner resubmits.",
            )}
          </p>
        ) : null}
        <Button
          type="button"
          size="sm"
          className="self-start"
          disabled={
            moveToRevisionDisabled || draft.trim() === "" || sendRevision.isPending
          }
          onClick={handleSend}
        >
          {sendRevision.isPending
            ? t("scriptReview.partnerRevision.sending", "Sending to partner…")
            : t("scriptReview.partnerRevision.send", "Send back to partner for revision")}
        </Button>
      </div>
    </section>
  );
}
